export type GwpConfigEntry = {
  id?: string;
  handle?: string;
  title?: string;
  description?: string;
  image?: string;
  product_id?: number | string;
  variant_id?: number | string;
  quantity_trigger?: number;
  /** @deprecated Prefer cart_price_trigger_cents. Major currency units (e.g. 75 = $75). */
  cart_price_trigger?: number;
  /** Cart spend threshold in cents (e.g. 7500 = $75). 0 = no price gate. */
  cart_price_trigger_cents?: number;
  collection_id?: number | string;
  collection_handle?: string;
  collection_product_ids?: (number | string)[];
};

/** Each GWP config may only add this many gift units to the cart. */
export const GWP_MAX_LINE_QUANTITY = 1;

export function getGwpConfigs(): GwpConfigEntry[] {
  const raw = (window as Window & { gwp_configs?: GwpConfigEntry[] }).gwp_configs;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((cfg) => cfg?.variant_id != null && cfg.variant_id !== "");
}

export function gwpConfigKey(cfg: GwpConfigEntry): string {
  return String(cfg.id ?? cfg.handle ?? cfg.variant_id ?? "");
}

export function isFreeProductProperty(value: unknown): boolean {
  return value === 1 || value === "1" || value === true;
}

export function parseGwpExcludeTags(raw: unknown): string[] {
  if (typeof raw !== "string" || raw.trim() === "") {
    return [];
  }

  return raw
    .replace(/\r\n/g, ",")
    .replace(/\n/g, ",")
    .replace(/\r/g, ",")
    .split(",")
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean);
}

export function customerHasGwpExcludeTag(
  customerTags: unknown,
  excludeRaw: unknown
): boolean {
  if (!Array.isArray(customerTags) || customerTags.length === 0) {
    return false;
  }

  const excludeTags = parseGwpExcludeTags(excludeRaw);
  if (excludeTags.length === 0) {
    return false;
  }

  return customerTags.some((tag) => {
    if (typeof tag !== "string") {
      return false;
    }

    return excludeTags.includes(tag.trim().toLowerCase());
  });
}

export function isGwpCustomerExcluded(): boolean {
  const win = window as Window & {
    gwp_customer_excluded?: boolean;
    gwp_exclude_customer_tags?: string;
    customer_tags?: string[];
  };

  if (win.gwp_customer_excluded === true) {
    return true;
  }

  return customerHasGwpExcludeTag(
    win.customer_tags,
    win.gwp_exclude_customer_tags
  );
}

export function isGwpGiftVariantId(variantId: string | number): boolean {
  return getGwpConfigs().some(
    (cfg) => String(cfg.variant_id) === String(variantId)
  );
}

export function collectionProductIdSet(cfg: GwpConfigEntry): Set<string> {
  const ids = cfg.collection_product_ids ?? [];
  return new Set(ids.map((id) => String(id)));
}

export function giftLinesForConfig(
  cartItems: any[],
  cfg: GwpConfigEntry
): any[] {
  const giftVid = String(cfg.variant_id);
  const configKey = gwpConfigKey(cfg);

  return cartItems.filter((item) => {
    if (!item || String(item.variant_id) !== giftVid) {
      return false;
    }

    if (!isFreeProductProperty(item.properties?.["_free-product"])) {
      return false;
    }

    const lineConfigId = item.properties?.["_gwp_config_id"];
    if (lineConfigId == null || lineConfigId === "") {
      return true;
    }

    return String(lineConfigId) === configKey;
  });
}

export function totalGiftQuantity(lines: any[]): number {
  return lines.reduce(
    (sum, item) => sum + (Number(item?.quantity) || 0),
    0
  );
}

export function clampGwpQuantity(quantity: number): number {
  return Math.min(Math.max(0, quantity), GWP_MAX_LINE_QUANTITY);
}
